import type {DoppeActionType} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeActionTypeProduct} from '../doppe-action-type-product/doppe-action-type-product';
import {
    convertDoppeWixProductToProductSettings,
    convertDoppeWixProductToProductSettingsWithDefaultTexts,
    DoppeActionTypeProductAdditionalSectionsDisplay,
    doppeActionTypeProductActionContentSettingsMetadata,
    doppeActionTypeProductResolveSettingsFromBoundEntity
} from '../doppe-action-type-product/doppe-action-type-product';
import type {HideableValue, OverridableValue, RichTextContent} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullishValues,
    arrayRemoveNullValues,
    arraySkipTake,
    asArray,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisibleAndNotEqualValue,
    hideableValueModifyValue,
    immerProduce,
    iterableMapToArray,
    overridableValueCreateOriginal,
    overridableValueGetOverriddenValue,
    overridableValueGetValue,
    overridableValueIsOriginal
} from '@wix/devzai-utils-common';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    DoppeDtoStoreProduct,
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    doppeListActionListItemSettingsMigrateBaseSettings,
    DoppeListActionPartialSettings,
    DoppeListActionSettings,
    DoppeListDataItemsSource,
    DoppeProductsExternalDataSource
} from '../../client-server-common';
import {
    BymoPagePropsSpecialContentSettingMetadata,
    convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction,
    DoppeActionLayout,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsNotFound,
    doppeDtoProductGetMediaItems,
    DoppeExternalProductProvider,
    doppeExternalProductProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemSettingsCreateDefault,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithSectionsMigration,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
    DoppeProductsExternalDataSourceType,
    doppeProductsExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common';
import {bymoPageContextPropsResolveBusinessEntity, bymoPageContextPropsResolveBusinessEntityListItems} from '../../doppe-sdk/bymo-page/bymo-page-context-props';
import {DoppeSupportedWixTpas} from '../../client-server-common/types/doppe-supported-wix-tpas';
import {DoppeDtoUserMetaSiteType} from '../../client-server-common/types/doppe-dto-user';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeAppPageContentLocales} from '../../client/doppe-app-locales/doppe-app-page-content-locales';
import {DoppeActionCTAType, doppeActionCtaButtonsResetSettingsForLock} from '../../client-server-common/types/doppe-action-cta-button';
import type {DoppeActionCtaButtonSettings} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration,
    getDefaultDoppeActionCtaButtonSettings
} from '../../client-server-common/types/doppe-action-cta-button-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';


import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';


export namespace DoppeActionTypeProducts {

    export interface ProductSettings extends
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeActionTypeProduct.ProductSettings {
        productTitle: OverridableValue<string>;
    }

    export interface ActionSettings extends
        DoppeListActionSettings<ProductSettings>,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeProductsExternalDataSource>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings {
        itemShowPrice: boolean;
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
        displayAdditionalSections: DoppeActionTypeProductAdditionalSectionsDisplay;
    }

    export interface DeprecatedFields extends Partial<DoppeListActionSettings.WithCtaButton> {
        products?: ProductSettings[];
        previewProductButtonText?: string;
    }

    export interface DeprecatedItemFields extends Partial<DoppeActionCtaButtonSettings> {
        productButtonText?: string;
    }

    export type PartialProps = DoppeListActionPartialSettings<ActionSettings, ProductSettings, DeprecatedFields, DeprecatedItemFields>;
}

const doppeActionTypeProductsSettingsMetadata = {
    contentSettingsMetadata: {
        ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
        ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            itemShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowPrice'}),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            previewShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowPrice'}),
            previewShowCoupon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowCoupon'}),
        externalDataSourceItemsCount: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    },
    supportsActionCtaButtons: {
        customCtaButtonTypes: {
            [DoppeActionCTAType.BuyNow]: true
        }
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        mainTitle: true,
        mainImage: true
    },
    bindableContentSettingsNames: {}
} satisfies DoppeActionType.SettingsMetadata<DoppeActionTypeProducts.ActionSettings>;

const doppeActionTypeProductsListItemSettingsMetadata = {
    contentSettingsMetadata: doppeActionTypeProductActionContentSettingsMetadata,
    supportsActionCtaButtons: {
        customCtaButtonTypes: {
            [DoppeActionCTAType.BuyNow]: true
        }
    },
    supportsContentSections: {
        customContentSectionsTypes: {}
    },
    hideableContentSettingsNames: {
        description: true,
        couponCode: true,
        ribbon: true,
        priceData: true
    },
    bindableContentSettingsNames: {
        description: true,
        priceData: true,
        productImage: true,
        productTitle: true,
        ribbon: true
    }
} satisfies DoppeActionType.ListItemsSettingsMetadata<DoppeActionTypeProducts.ActionSettings>

export const doppeActionTypeProducts = doppeActionTypeDefineMetadata<DoppeActionTypeProducts.ActionSettings, DoppeActionTypeProducts.PartialProps>({
    id: 'io.bymo.action/products',
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            ctaButton,
            itemShowPrice = true,
            previewShowCoupon = true,
            previewShowPrice = true,
            previewShowDescription = true,
            previewShowTitle = true,
            displayAdditionalSections = DoppeActionTypeProductAdditionalSectionsDisplay.None,
            layout = DoppeActionLayout.Column,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            itemShowPrice: itemShowPrice,
            previewShowCoupon: previewShowCoupon,
            previewShowPrice: previewShowPrice,
            previewShowDescription: previewShowDescription,
            previewShowTitle: previewShowTitle,
            displayAdditionalSections: displayAdditionalSections,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings<DoppeActionTypeProducts.ProductSettings>({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(product => {

                    const {
                        productButtonText,
                        ctaButton,
                        productTitle = '',
                        productPageLink= '',
                        productImage = null,
                        productProvider = null,
                        productProviderId = null,
                        ribbon = hideableValueCreateHidden(''),
                        description = hideableValueCreateHidden(''),
                        couponCode = hideableValueCreateHidden(''),
                        priceData = hideableValueCreateHidden({actualPrice: '', discountPrice: ''}),
                        ...restProductSettings
                    } = product;

                    return {
                        ...restProductSettings,
                        ...doppeListActionListItemSettingsMigrateBaseSettings(product),
                        productProvider: productProvider,
                        productProviderId: productProviderId,
                        ribbon: ribbon,
                        productTitle: productTitle,
                        couponCode: couponCode,
                        productPageLink: productPageLink,
                        description: description,
                        productImage: productImage,
                        ...getDefaultDoppeActionCtaButtonSettings({
                            buttonText: productButtonText,
                            showButton: true
                        }),
                        ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                            hasSourceURL: true,
                            ctaButton: ctaButton,
                        }),
                        ...doppeListActionListItemWithCtaButtonsMigration(product, true),
                        ...doppeListActionListItemWithSectionsMigration(product),
                        priceData: hideableValueModifyValue(priceData, overridableValue => {
                            const innerValue = overridableValueGetValue(overridableValue);
                            const resolved = {...innerValue, discountPrice: innerValue.discountPrice ?? ''};
                            return overridableValueIsOriginal(overridableValue)
                                ? overridableValueCreateOriginal(resolved)
                                : resolved;
                        }),
                    };
                }),
            }),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({
                defaultTitle: ''
            }, action.settings),
            ...convertCommonListSettingToDefaultDoppeActionCtaButtonSettingsFromListAction(action.settings),
            ...doppeActionCtaButtonTypeGetDefaultCtaButtonSettingsMigration({
                ctaButton: ctaButton,
                hasSourceURL: false
            }),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, true),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeProducts.ActionSettings;
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    actionSettingsMetadata: doppeActionTypeProductsSettingsMetadata,
    actionListItemSettingsMetadata: doppeActionTypeProductsListItemSettingsMetadata,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
            const productImage = overridableValueGetValue(product.productImage);
            return productImage ? asArray(productImage) : skip
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: (actionSettings, bymoPageContextProps) => {
        const firstItem = actionSettings.listDataItems[0];
        const storeProduct = firstItem?.productProvider && firstItem?.productProviderId ? (() => {
            const entityDto = bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                doppeExternalProductProviderResolveBusinessEntityReference(firstItem.productProvider!, firstItem.productProviderId!)
            );
            return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
        })() : null;

        const firstProductImage = overridableValueGetOverriddenValue(
            firstItem?.productImage,
            storeProduct ? doppeDtoProductGetMediaItems(storeProduct) : null
        );
        const productImage = firstProductImage ? asArray(firstProductImage)[0] ?? null : null;
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, productImage);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {externalDataSource, externalDataSourceItemsCount} = actionSettings;
        if (externalDataSource !== null) {
            const businessEntityRef = doppeProductsExternalDataSourceResolveBusinessEntityReferences(externalDataSource);
            const externalProducts = bymoPageContextPropsResolveBusinessEntityListItems(bymoPageContextProps, businessEntityRef);

            console.log('[DEBUG resolveActualActionSettings]', {
                externalDataSourceType: externalDataSource.type,
                categoryIds: (externalDataSource as any).categoryIds,
                businessEntityRef: JSON.stringify(businessEntityRef),
                externalProductsCount: externalProducts.length,
                businessEntitiesDataCount: bymoPageContextProps.businessEntitiesData?.length ?? 0,
            });

            const isHoppSource = externalDataSource.type === DoppeProductsExternalDataSourceType.LatestHoppProducts ||
                externalDataSource.type === DoppeProductsExternalDataSourceType.HoppBestSellerProducts ||
                externalDataSource.type === DoppeProductsExternalDataSourceType.HoppProductsByCategories;

            const listDataItems = arraySkipTake(externalProducts, 0, externalDataSourceItemsCount).map(doppeDtoProduct => {
                return convertDoppeWixProductToProductsSettings(doppeDtoProduct, isHoppSource ? DoppeExternalProductProvider.Hopp : DoppeExternalProductProvider.Wix);
            });

            return {
                ...actionSettings,
                listDataItems: listDataItems
            };
        }
        return actionSettings;
    },
    resolveActualListItemSettings: (listItemId, actionSettings, bymoPageContextProps) => {
        const listItem = actionSettings.listDataItems.find(item => item.id === listItemId);
        if (!listItem) return undefined;

        const {productProvider, productProviderId} = listItem;
        if (productProvider === null || productProviderId === null) return listItem;

        const boundProduct = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId)
        );

        if (!boundProduct || doppeBusinessEntityDtoIsNotFound(boundProduct)) return undefined;

        return {
            ...listItem,
            ...doppeActionTypeProductResolveSettingsFromBoundEntity(listItem, boundProduct as DoppeDtoStoreProduct),
        } as DoppeActionTypeProducts.ProductSettings;
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            const ref = doppeProductsExternalDataSourceResolveBusinessEntityReferences(externalDataSource);
            console.log('[DEBUG resolveUsedBusinessEntities] externalDataSource:', {type: externalDataSource.type, categoryIds: (externalDataSource as any).categoryIds, ref: JSON.stringify(ref)});
            return [ref];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (product, skip) => {
                return product.productProvider !== null && product.productProviderId !== null ?
                    doppeExternalProductProviderResolveBusinessEntityReference(
                        product.productProvider,
                        product.productProviderId
                    ) : skip
            });
        }
    },
    resolveListDataItemsSource: actionSettings => {
        const {externalDataSource} = actionSettings;
        return externalDataSource !== null
            ? {type: DoppeListDataItemsSource.Dynamic, source: doppeProductsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)}
            : {type: DoppeListDataItemsSource.Static};
    },
    getSupportedDataSources: ({hasLinkedMetaSiteTpaInstalled, hasCreatorsMetaSiteTpaInstalled}) => {
        return [
            ...hasLinkedMetaSiteTpaInstalled ? [
                {type: DoppeProductsExternalDataSourceType.LatestWixProducts} as const,
                {type: DoppeProductsExternalDataSourceType.WixBestSellerProducts} as const,
                {type: DoppeProductsExternalDataSourceType.WixProductsByCategories, categoryIds: [] as string[]},
            ] : [],
            ...hasCreatorsMetaSiteTpaInstalled ? [
                {type: DoppeProductsExternalDataSourceType.LatestHoppProducts} as const,
                {type: DoppeProductsExternalDataSourceType.HoppBestSellerProducts} as const,
                {type: DoppeProductsExternalDataSourceType.HoppProductsByCategories, categoryIds: [] as string[]},
            ] : [],
        ];
    },
    resolveListItemEntityBinding: (listItemSettings, _actionSettings) => {
        const {productProvider, productProviderId} = listItemSettings;
        return productProvider !== null && productProviderId !== null
            ? doppeExternalProductProviderResolveBusinessEntityReference(productProvider, productProviderId)
            : null;
    },
    getSupportedListItemBoundEntities: ({linkedMetaSiteInstalledTpas, creatorsMetaSiteInstalledTpas}) =>
        arrayRemoveNullishValues([
            linkedMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.StoresNew) ? DoppeBusinessEntity.WixProduct : undefined,
            creatorsMetaSiteInstalledTpas.includes(DoppeSupportedWixTpas.StoresNew) ? DoppeBusinessEntity.HoppProduct : undefined,
        ]),
    getSupportedListItemBoundEntitiesByTpa: () => ({
        [DoppeDtoUserMetaSiteType.LinkedMetaSite]: {
            [DoppeSupportedWixTpas.StoresNew]: [DoppeBusinessEntity.WixProduct],
        },
        [DoppeDtoUserMetaSiteType.CreatorsMetaSite]: {
            [DoppeSupportedWixTpas.StoresNew]: [DoppeBusinessEntity.HoppProduct],
        },
    }),
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveCouponDataFromListItem: listItem => {
        return {
            showCouponCode: hideableValueIsVisibleAndNotEqualValue(listItem.couponCode, ''),
            couponCode: hideableValueGetVisibleValue(listItem.couponCode, ''),
            couponDescription: '',
            couponTitle: listItem.title ?? '',
        }
    },
    resolveListItems: (action, _viewerLang, _bymoPageProps, _viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            const storeProduct = item.productProvider && item.productProviderId ? (() => {
                const entityDto = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalProductProviderResolveBusinessEntityReference(item.productProvider!, item.productProviderId!)
                );
                return entityDto && !doppeBusinessEntityDtoIsNotFound(entityDto) ? entityDto as DoppeDtoStoreProduct : null;
            })() : null;

            const resolvedProductImage = overridableValueGetOverriddenValue(
                item.productImage,
                storeProduct ? doppeDtoProductGetMediaItems(storeProduct) : null
            );

            return {
                title: overridableValueGetOverriddenValue(item.productTitle, storeProduct?.name ?? ''),
                action: action,
                couponCode: item.couponCode,
                description: hideableValueModifyValue(
                    item.description,
                    value => overridableValueGetOverriddenValue(value, storeProduct?.descriptionPlainText ?? '')
                ) as HideableValue<RichTextContent>,
                image: resolvedProductImage ? asArray(resolvedProductImage)[0] : null,
                itemId: item.id,
                ribbon: hideableValueModifyValue(
                    item.ribbon,
                    value => overridableValueGetOverriddenValue(value, storeProduct?.ribbon ?? '')
                ) as HideableValue<string>,
                url: item.productPageLink,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                const productImage = overridableValueGetValue(item.productImage);
                if (productImage) {
                    const images = asArray(productImage);
                    item.productImage = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    });
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    },
});

export function convertDoppeWixProductToProductsSettingsWithDefaultTexts(
    product: DoppeDtoStoreProduct,
    pageContentLocales: DoppeAppPageContentLocales,
    productProvider: DoppeExternalProductProvider = DoppeExternalProductProvider.Wix,
): DoppeActionTypeProducts.ProductSettings {
    const productSettings = convertDoppeWixProductToProductSettingsWithDefaultTexts({
        product: product,
        pageContentLocales: pageContentLocales,
        productProvider: productProvider,
    });

    const {
        ctaButtons,
        sections,
        ...restSettings
    } = productSettings;

    return {
        ...restSettings,
        productTitle: overridableValueCreateOriginal(product?.name ?? pageContentLocales["product.actionSettings.title"]),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemSettingsCreateDefault(product.id),
    }
}

export function convertDoppeWixProductToProductsSettings(
    product: DoppeDtoStoreProduct,
    productProvider?: DoppeExternalProductProvider,
): DoppeActionTypeProducts.ProductSettings {
    const productSettings = convertDoppeWixProductToProductSettings({
        product: product,
        productProvider: productProvider,
    });

    return {
        ...productSettings,
        productTitle: overridableValueCreateOriginal(product?.name),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionListItemSettingsCreateDefault(product.id),
    }
}